// ======================================================================
// File Name       : ItemTreeContainer
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as containerActions from '../../actions/ContainerAction';
import * as modalActions from '../../actions/ModalAction';
import TreeWithItemControl from '../../components/TreeWithItemControl';
import ModalWrapper from '../../components/modal/ModalWrapper';
import { STRUCTURE_TYPE } from '../../constants';
import { CONTENT_TYPE, DATA_AFFIILIATION_TYPE } from '../../constants/ContentConstant';
import * as modalIds from '../../constants/ModalIds';
import ICON_BOX from '../../images/tree/box-icon.png';
import ICON_COURSE_EXTERNAL from '../../images/tree/course-icon-external.png';
import ICON_COURSE from '../../images/tree/course-icon.png';
import ICON_DOCUMENT from '../../images/tree/document-24.png';
import ICON_DRILL from '../../images/tree/drill-icon.png';
import ICON_LINK from '../../images/tree/link.png';
import ICON_VIDEO from '../../images/tree/video-icon.png';
import { hideLoading } from '../../utils/PageUtils';

const initCourseTree = false;

class ItemTreeContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedItem: {},
    };
    this.onOK = this.onOK.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.hideLoading = this.hideLoading.bind(this);
    this.handleSelectCheckBox = this.handleSelectCheckBox.bind(this);
  }

  // Step 10.2.0 START
  componentDidMount() {
    const { container: { structureType } } = this.props;
    let targetModalId;
    switch (structureType) {
      case STRUCTURE_TYPE.COURSE:
        targetModalId = modalIds.COURSE_REGISTER_EDIT;
        break;
      case STRUCTURE_TYPE.BOX:
        targetModalId = modalIds.BOX_REGISTER_EDIT;
        break;
      case STRUCTURE_TYPE.ITEM:
        targetModalId = modalIds.CONTENT_LINKING_REGISTER_EDIT_DETAIL_SETTING;
        break;
      default:
        break;
    }
    this.setState({
      targetModalId,
    });
  }
  // Step 10.2.0 END

  getModalOptions() {
    const { container: { structureType } } = this.props;
    return {
      // Step 10.2.0 START
      title:
        structureType === STRUCTURE_TYPE.COURSE
          ? "完了条件のアイテムを選択してください"
          : "開始条件のアイテムを選択してください",
      // Step 10.2.0 END
      footerBtns: [
        { name: "キャンセル", onClick: this.onCancel, variant: 'default' },
        { name: "OK", onClick: this.onOK, variant: 'primary' },
      ],
    };
  }

  onOK() {
    const { targetModalId } = this.state;
    const { dispatch } = this.props;
    dispatch(containerActions.selectedItemOnDialog(this.state.selectedItem));
    dispatch(modalActions.openModal(targetModalId));
  }

  onCancel() {
    const { targetModalId } = this.state;
    const { dispatch } = this.props;
    dispatch(modalActions.openModal(targetModalId));
  }

  /**
   * Create node
   * Based on "structure" in param, create corresponding JStree's node
   * - Node's text will show structureName
   * - Icon and menu is determined by structureType
   * - Functions are in menu checked whether user can access
   * - OnClick: passing "structure" as "callbackData" to callback function
   * @param {Object} param
   */
  createNode({ structure }) {
    const {
      structureType,
      structureSecernId,
      structureName,
      contentType,
      hasSubStructures,
      dataAffiliationType } = structure;
    /**
     * Determine icon and functions of node
     */
    let icon = false;
    let checkboxClassName = '';

    switch (structureType) {
      case STRUCTURE_TYPE.COURSE:
        icon = dataAffiliationType === DATA_AFFIILIATION_TYPE.EXTERNAL ?
        ICON_COURSE_EXTERNAL : ICON_COURSE;
        checkboxClassName = 'no-checkbox';
        break;
      case STRUCTURE_TYPE.BOX:
        icon = ICON_BOX;
        checkboxClassName = 'no-checkbox';
        break;
      case STRUCTURE_TYPE.ITEM:
        switch (contentType) {
          case CONTENT_TYPE.VIDEO:
            icon = ICON_VIDEO;
            break;
          case CONTENT_TYPE.DRILL:
            icon = ICON_DRILL;
            break;
          case CONTENT_TYPE.DOCUMENT:
            icon = ICON_DOCUMENT;
            break;
          case CONTENT_TYPE.LINK:
            icon = ICON_LINK;
            break;
          default:
            break;
        }
        break;
      default:
        icon = false;
    }

    const { edittingItem } = this.props.item;
    // in item regist/edit case, check the selected item is disabled or not
    const isItemEditting =
      this.props.container.structureType === STRUCTURE_TYPE.ITEM && structureSecernId === edittingItem.itemSecernId;
    const { selectedItemList } = this.props.container;
    let selectedItemCheck = selectedItemList;
    if (selectedItemList) {
      selectedItemCheck = selectedItemList.reduce((ids, cur) => {
        ids.push(cur.itemSecernId);
        return ids;
      }, []);
    }
    const isChecked = selectedItemCheck.indexOf(structureSecernId) > -1;
    /**
     * JSTree node's data
     */
    return {
      id: structureSecernId,
      text: structureName,
      icon,
      data: {
        menu: [],
        callbackData: structure,
      },
      children: [],
      state: {
        disabled: isItemEditting || isChecked,
        checked: isChecked,
      },
      a_attr: { class: `display-inline-block ${checkboxClassName}` },
    };
  }

  /**
   * Build course tree
   * This function will be started from list of courseSecernId
   * By courseSecernId find and build entire box, item nodes below
   * On current development, structureType=[2:item] is a leaf node
   * so just only perform searching child node on type=[1:box]
   */
  createCourseTree() {
    const { structuresByItem } = this.props.container;

    /**
     * Sort list by displayOrder and structureSecernId
     * @param {Array} listOfNode
     */
    const sortByDisplayOrderAndStructureSecernId = (listOfNode) => {
      const sortedList = [...listOfNode];
      sortedList.sort((curNode, nextNode) => {
        const curNodeData = curNode.data.callbackData;
        const nextNodeData = nextNode.data.callbackData;
        if (curNodeData.structureType === STRUCTURE_TYPE.BOX &&
          nextNodeData.structureType === STRUCTURE_TYPE.ITEM) {
          return -1;
        }
        if (curNodeData.structureType === STRUCTURE_TYPE.ITEM &&
          nextNodeData.structureType === STRUCTURE_TYPE.BOX) {
          return 1;
        }
        if (curNodeData.displayOrder || nextNodeData.displayOrder) {
          if (curNodeData.displayOrder < nextNodeData.displayOrder) {
            return -1;
          }
          if (curNodeData.displayOrder > nextNodeData.displayOrder) {
            return 1;
          }
        }
        return curNodeData.structureSecernId.localeCompare(nextNode.structureSecernId);
      });
      return sortedList;
    };

    let tree = [],
      nodeMapper = {}, needtoSortNodes = {};

    for (var id in structuresByItem.bySecernId) {
      const node = structuresByItem.bySecernId[id];
      const arrElem = this.createNode({
        structure: node,
      });

      arrElem.parentCourseSecernId = node.parentCourseSecernId;
      arrElem.parentBoxSecernId = node.parentBoxSecernId;
      arrElem.children = [];
      nodeMapper[id] = arrElem;
    }

    for(var key in nodeMapper){
      const node = nodeMapper[key];
      if (node.parentCourseSecernId) {
        nodeMapper[node.parentCourseSecernId].children.push(node);
        needtoSortNodes[node.parentCourseSecernId] = nodeMapper[node.parentCourseSecernId];
      } else 
      if (node.parentBoxSecernId) {
        nodeMapper[node.parentBoxSecernId].children.push(node);
        needtoSortNodes[node.parentBoxSecernId] =  nodeMapper[node.parentBoxSecernId];
      }
      else {
        tree.push(node);
      }
    }

    //sort course
    tree = sortByDisplayOrderAndStructureSecernId(tree);

    //sort box, item
    for (var key in needtoSortNodes) {
      const node = needtoSortNodes[key];
      if (node.children && node.children.length > 0) {
        node.children = sortByDisplayOrderAndStructureSecernId(node.children);
      }
    }

    //structures
    return tree;

  }

  shouldComponentUpdate(nextProps, nextState) {
    // check tree is init or not to prevent render many times
    let isInitiated = false;
    if (nextProps.isInitiated) {
      if (!this.initCourseTree) {
        isInitiated = nextProps.isInitiated;
        this.initCourseTree = nextProps.isInitiated;
      }
    } else {
      this.initCourseTree = nextProps.isInitiated;
    }
    const shouldNotUpdate =
    nextProps.container.structures === this.props.container.structures &&
    nextProps.container.structures.bySecernId === this.props.container.structures.bySecernId &&
    nextProps.container.structures.courseSecernIds === this.props.container.structures.courseSecernIds;
    return isInitiated || (nextProps.isInitiated && !shouldNotUpdate);
  }

  hideLoading() {
    this.props.dispatch(hideLoading());
  }

  handleSelectCheckBox(event, node) {
    if (!node) return;
    this.setState({
      selectedItem: node,
    });
  }
  render() {
    // Build course nodes
    let nodes = [];
    if (this.initCourseTree) {
      nodes = this.createCourseTree();
    }
    return (
      <ModalWrapper
        {...this.getModalOptions()}
        hideModal={this.props.hideModal}
      >
        <TreeWithItemControl
          data={nodes}
          ref={(instance) => {
            this.treeInstance = instance;
          }}
          hideLoading={this.hideLoading}
          checkbox={true}
          openAll
          onSelectCheckbox={(e, node) => {
            let selectedItemIds = [];
            // add if selected node is item
            if (node && node.selected) {
              selectedItemIds = node.selected;
            }
            this.handleSelectCheckBox(e, selectedItemIds);
          }}
        />
      </ModalWrapper>
    );
  }
}

ItemTreeContainer.propTypes = {
  dispatch: PropTypes.func.isRequired,
  container: PropTypes.shape().isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const { container, authed, item, box } = state;
  const { authedUser } = authed;
  const { userInfo } = authedUser;
  const { form } = state;
  return {
    container,
    displayUserType: userInfo ? userInfo.displayUserType : null,
    isInitiated: container.isInitiated,
    form,
    item,
    box,
  };
};

export default connect(mapStateToProps, null, null, { withRef: true })(ItemTreeContainer);